import { useCallback, useMemo, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  deleteIdState,
  pageState,
  pageSizeState,
  useCreateMutation,
  useDestroyMutation,
  useTeamsFetchingQuery,
  useTeamsPagesQuery,
  useTeamsQuery,
  teamsIdState,
  teamsTermState,
} from "./Teams.state";
import { Button, HeaderRow, Row } from "../App.styles";
import { Pagination, Search, SidebarLayout, Spinner } from "../common";
import { Refresher } from "../common";
import { usePageListeners } from "../common/Refresher.state";
import ROUTE_PATTERNS from "../consts/routes";
import DeleteModal from "./DeleteModal";
import TeamList from "./TeamList";
import Team from "./Team";
import {
  Administrators,
  Banned,
  Blast,
  Coordinators,
  Invitations,
  SuperAdmin,
} from "./sidebar-cards";
import TeamsPlaceholder from "./TeamsPlaceholder";
import { withPlaceholder } from "../common";
import { alertState } from "../common/AlertDisplay/AlertDisplay.state";
import AlertDisplay from "../common/AlertDisplay";

/**
 * The main Teams dashboard component.
 * Displays a paginated list of teams with search functionality.
 * Includes a sidebar with administrative controls (Banned, Administrators, Blast, etc.).
 * Allows creating new teams and deleting existing ones.
 * 
 * @returns {React.ReactElement} The rendered Teams dashboard.
 */
const Teams = () => {
  const { t } = useTranslation();
  const { game_id } = useParams();
  const [teamId] = useRecoilState(teamsIdState);
  const teams = useTeamsQuery(game_id);
  const pages = useTeamsPagesQuery();
  const setAlert = useSetRecoilState(alertState);
  const [deleteId, setDeleteId] = useRecoilState(deleteIdState);
  const [term, setTerm] = useRecoilState(teamsTermState);
  const [page, setPage] = useRecoilState(pageState);
  const [pageSize, setPageSizeState] = useRecoilState(pageSizeState);
  const [scrollId, setScrollId] = useState(teamId);
  const teamIds = useMemo(() => teams?.map(team => team.id), [teams]);
  const listeners = usePageListeners(ROUTE_PATTERNS.TEAMS_ORGANIZE, { teamIds });

  const { isCreating, create } = useCreateMutation();
  const { isDeleting, destroy } = useDestroyMutation();
  const isFetching = useTeamsFetchingQuery();
  const setPageSize = pageSize => {
    setPageSizeState(Number(pageSize))
  }
  const onCreate = useCallback(async () => {
    const updatePage = (currentPage) => {
      if (Number(currentPage) !== Number(page)) {
        setPage(currentPage)
      }
    }
    try {
      const { data } = await create();
      updatePage(data?.meta?.page)
      setScrollId(data.team.id);
    } catch (e) {
      if (e.response.status === 402) {
        setAlert({
          type: "error",
          message:
            t("By default your game can accommodate 1 team. In order to allow more teams to participate, you will need to purchase a license."),
        });
      } else {
        throw e;
      }
    }
  }, [create, setAlert, page, setPage, t]);

  const search = (query) => {
    setPage(1)
    setTerm(query)
  }

  return (
    <>
      <AlertDisplay />
      <HeaderRow className="flex-grow-1 mb-2 g-2">
        <div className="col-12 col-lg-9 mb-2 mb-lg-0">
          <div className="row">
            <div className="col-12 col-lg-9 mb-2 d-block d-sm-flex">
              <Search className="form-control--flat form-control--flat-sm m-0" onChange={search} value={term} />
            </div>
            <div className="col-12 col-lg-3 mb-2 d-block d-sm-flex">
              <Button className="flex-grow-1" color="primary" onClick={onCreate} disabled={isCreating}>
                {t('Add New Team')} {isCreating && <Spinner size="0.875rem" />}
              </Button>
            </div>
          </div>
        </div>
      </HeaderRow>
      <Row>
        <SidebarLayout
          message={t("Setup teams in advance or change them up during the game.")}
          panel={(
            <>
              <Banned />
              <Administrators />
              <SuperAdmin />
              <Coordinators />
              <Invitations />
              <Blast />
            </>
          )}
        >
          <TeamList>
            { teams && teams.map((row, index) => (
              <Team
                index={index}
                key={row.id}
                {...row}
                onDelete={setDeleteId}
                game_id={game_id}
                scrollId={scrollId}
              />
            ))}
          </TeamList>
        </SidebarLayout>
      </Row>
      <Row>
        <Pagination
          loading={isFetching}
          page={page}
          pageSize={pageSize}
          pages={pages}
          setPage={setPage}
          setPageSize={setPageSize}
        />
      </Row>
      <DeleteModal
        loading={isDeleting}
        show={!!deleteId}
        onConfirm={destroy}
        onDismiss={() => setDeleteId(0)}
      />
      <Refresher 
        listeners={listeners}
        refreshCallbacks={{
          "ss.team.create": (id) => {
            setAlert({
              id: id,
              type: "info",
              message: t("The view has been refreshed. It may or may not include the new team."),
              closable: true,
              autoClose: true,
            })
          },
        }}
      />
    </>
  );
};

export default withPlaceholder(Teams, TeamsPlaceholder);
